import { createApp, h } from 'vue';
import { createInertiaApp } from '@inertiajs/vue3';
import { Head, Link, router } from '@inertiajs/vue3'
import  VueGoogleMaps from '@fawmi/vue-google-maps'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { appBase, appUrl } from './appUrl'

// FontAwesome setup — tree-shaken Pro icon registrations live in ./fontawesome.js
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import './fontawesome.js'


// Check for browser's back and forward actions
window.onpopstate = function(event) {
    if (event.type === 'popstate') {
        location.reload()
    }
};

// Ensure axios sends proper headers and pre-warm Sanctum CSRF cookie
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';
// Under rentrisk.com/app, root-relative requests ("/properties/1/...") must stay in the app.
if (appBase()) {
    axios.defaults.baseURL = appBase();
}
// Non-blocking prewarm of CSRF cookie so subsequent POSTs have valid token
axios.get('/sanctum/csrf-cookie').catch(() => { /* ignore prewarm errors */ });

const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'Laravel';

createInertiaApp({
    resolve: (name) => {
        const pages = import.meta.glob('./Pages/**/*.vue', { eager: true });
        const page = pages[`./Pages/${name}.vue`];
        if (!page) {
            throw new Error(`Page not found: ./Pages/${name}.vue`);
        }
        return page;
    },
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) })
            .use(plugin)
            .use(VueGoogleMaps, {
                load: {
                    key: import.meta.env.VITE_GOOGLE_PLACES_API_KEY,
                    libraries: 'places,maps',
                    v: 'weekly'
                },
            })
            .component('InertiaHead', Head)
            .component('InertiaLink', Link)
            .component('Dialog', Dialog)
            .component('DialogPanel', DialogPanel)
            .component('TransitionChild', TransitionChild)
            .component('TransitionRoot', TransitionRoot)
            .component('font-awesome-icon', FontAwesomeIcon)
            .mixin({ methods: { route, appUrl } });

        // Initialize Sentry browser SDK before mounting so unhandled errors
        // during the first render are captured. Server-side Sentry is configured
        // separately via SENTRY_LARAVEL_DSN and is unaffected by this block.
        if (import.meta.env.VITE_SENTRY_DSN_PUBLIC) {
            Sentry.init({
                app,
                dsn: import.meta.env.VITE_SENTRY_DSN_PUBLIC,
                environment: import.meta.env.VITE_APP_ENV || 'production',
                release: import.meta.env.VITE_APP_VERSION || 'unknown',

                // RAC-10011: sample rates env-driven so we can tune them
                // without redeploying code. Vite inlines these at build time —
                // env changes apply on the next deploy. Defaults match a
                // sensible baseline (10% traces / 0% session replays /
                // 30% error replays) so the file is safe to deploy without
                // env vars set.
                tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1'),

                replaysSessionSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? '0.0'),
                replaysOnErrorSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? '0.3'),

                integrations: [
                    Sentry.browserTracingIntegration(),
                    Sentry.replayIntegration({
                        maskAllText: true,
                        maskAllInputs: true,
                        blockAllMedia: true,
                    }),
                ],

                // RENT-RISK-9C: an Inertia visit whose XHR dies at the network layer
                // (phone locked, tab backgrounded, coverage dropped) rejects with
                // axios's bare "Network Error". There is nothing in our code to fix
                // for those, and they were 253 events of pure noise.
                // Anchored: a plain string matches any message containing it.
                ignoreErrors: [/^Network Error$/],

                beforeSend(event) {
                    if (import.meta.env.VITE_APP_ENV === 'local') {
                        return null;
                    }
                    return event;
                },
            });

            // Identify the signed-in user so Sentry's "users" count means something.
            // Id only, never email or any other personal field; the server side sends the same.
            const identifySentryUser = (pageProps) => {
                const user = pageProps?.auth?.user;
                Sentry.setUser(user ? { id: String(user.id) } : null);
            };
            identifySentryUser(props.initialPage.props);
            router.on('success', (event) => identifySentryUser(event.detail.page.props));
        }

        app.mount(el);

        return app;
    },
    title: (title) => `${title} - ${appName}`,
    progress: {
        color: '#4B5563',
        showSpinner: true,
    },
});

// Check and apply the user's stored preference or the system preference on page load
// if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
//     document.documentElement.classList.add('dark');
// } else {
//     document.documentElement.classList.remove('dark');
// }
//
// // User actions to explicitly choose themes
// function toggleTheme(isDark) {
//     localStorage.theme = isDark ? 'dark' : 'light'; // Save preference
//     document.documentElement.classList.toggle('dark', isDark);
// }
